/**
 * Free-lesson API client. Talks to /api/free-lesson/* — the self-serve
 * flow that gives anonymous visitors one trimmed lesson in exchange for
 * just an email. Reuses the shared axios client so cookies + 402/423
 * interceptors apply.
 */
import { api } from "./api";

export async function requestFreeLesson(payload) {
  const { data } = await api.post("/free-lesson/request", payload);
  return data; // { status: 'sent' | 'already_claimed', email? }
}

export async function resendFreeLesson(email) {
  const { data } = await api.post("/free-lesson/resend", { email });
  return data;
}

export async function fetchFreeLessonByToken(token) {
  const { data } = await api.get(`/free-lesson/by-token/${encodeURIComponent(token)}`);
  return data; // { status, lesson, grade, subject, transferable_skill, email }
}


/**
 * Lane 3A (Feb 2026) — anonymous, emailless taste. Inline generation:
 * server awaits the lightweight LLM call and returns the full lesson
 * payload + a token you can use to render / print later.
 */
export async function postAnonymousFreeLesson(payload) {
  const { data } = await api.post("/free-lesson/anonymous", payload);
  return data;
}

/**
 * Lane 3B (Feb 2026) — signed-in FREE-tier generation. Same lightweight
 * `_FREE_LESSON_TOOL` engine as the anonymous taste, but owned by the
 * user and counted against the monthly cap (5 / calendar month). Server
 * returns 429 with a `{code: "free_monthly_cap_reached", ...}` detail
 * body once the cap is reached — caller should catch and render the
 * upgrade nudge inline. Never hits `run_generate_job` / the full engine.
 */
export async function postAuthenticatedFreeLesson(payload) {
  const { data } = await api.post("/free-lesson/authenticated", payload);
  return data;
}

/** Fetch the caller's current free-lesson quota for this UTC month.
 *  Paid / trialing / beta users get `{unlimited: true, ...}`. */
export async function fetchFreeLessonQuota() {
  const { data } = await api.get("/free-lesson/quota");
  return data;
}

/** Fetch the signed-in user's own free lessons (Lane 3B). Newest first,
 *  capped at 60 rows. Excludes the heavy lesson_json blob. */
export async function fetchMyFreeLessons() {
  const { data } = await api.get("/free-lesson/mine");
  return data; // { lessons: [{ token, activity_name, grade, subject, ... }] }
}

/**
 * Lane 4 (Feb 2026) — persist a client-held free-lesson to the
 * signed-in user's mini-library. Enforces `SAVED_FREE_LESSON_LIMIT` on
 * the server; returns 409 with a `{code: "free_saved_cap_reached", ...}`
 * detail body when the user is at cap. Idempotent per (token, user_id).
 */
export async function saveFreeLesson(payload) {
  const { data } = await api.post("/free-lesson/save", payload);
  return data;
  // { token, already_saved, saved_used, saved_limit, saved_remaining }
}

/**
 * Lane 4 — remove one saved free lesson from the caller's mini-library.
 * Server scopes the delete filter to `{token, user_id,
 * origin: "authenticated_free"}` so anonymous rows and other users'
 * rows cannot be touched. Returns 404 if no such row exists for the
 * caller.
 */
export async function deleteMyFreeLesson(token) {
  const { data } = await api.delete(
    `/free-lesson/mine/${encodeURIComponent(token)}`,
  );
  return data;
}

/** Absolute URL to the branded printable PDF for a given token. */
export function freeLessonPdfUrl(token) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/free-lesson/by-token/${encodeURIComponent(token)}/pdf`;
}
